/**
 * =============================================================================
 * PATIENT JOURNEY - CONFIRMATIONS
 * =============================================================================
 *
 * Decides when the single `JourneyConfirmDialog` opens, and with what copy.
 * Used by the case details page and the Close Case popup alike, so both show
 * the same three dialogs with the same wording.
 *
 *   LEAVE         the user dismisses the popup while steps are ongoing
 *   CLOSE_MIDWAY  an option flagged `confirmBeforeClose` is about to close the
 *                 case before the journey has run its course
 *   CLOSED        the case has just closed, whether by finishing every step or
 *                 by an End Case option along the way
 * =============================================================================
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CONFIRM_TONE } from "./JourneyConfirmDialog";

export const CONFIRM_TYPE = {
    LEAVE: "leave",
    CLOSE_MIDWAY: "close-midway",
    CLOSED: "closed",
};

/**
 * @param {object}   params
 * @param {object}   params.workflow  the `useCaseWorkflow` instance
 * @param {object}   params.caseData  for the patient name and reference number
 * @param {function} params.onExit    dismiss the surrounding popup, if any
 */
export default function useJourneyConfirmations({
    workflow,
    caseData,
    onExit,
} = {}) {
    const [confirmType, setConfirmType] = useState(null);

    /* A case that was already closed when the screen opened must not pop the
       success dialog, so only the transition into closed counts.

       The journey is fetched, so "not closed" is also what an unloaded workflow
       looks like. The first reading is therefore only recorded, never treated as
       a transition - otherwise opening an already closed case would announce
       that it had just been closed. */
    const wasClosed = useRef(null);

    useEffect(() => {
        if (!workflow?.isReady) {
            return;
        }

        const isClosed = Boolean(workflow.isClosed);

        if (wasClosed.current === null) {
            wasClosed.current = isClosed;

            return;
        }

        if (!wasClosed.current && isClosed) {
            setConfirmType(CONFIRM_TYPE.CLOSED);
        }

        wasClosed.current = isClosed;
    }, [workflow?.isClosed, workflow?.isReady]);

    const dismiss = useCallback(() => setConfirmType(null), []);

    const exit = useCallback(() => {
        setConfirmType(null);

        if (typeof onExit === "function") {
            onExit();
        }
    }, [onExit]);

    /* -------------------------------------------------------------------------
       SAVING A STEP

       Pass this to the stepper instead of `workflow.submitStep` so an option
       that needs confirming is intercepted before it commits.
    ------------------------------------------------------------------------- */

    const submitStep = useCallback(() => {
        if (!workflow) {
            return;
        }

        if (workflow.selectedOption?.confirmBeforeClose && workflow.canSubmit) {
            setConfirmType(CONFIRM_TYPE.CLOSE_MIDWAY);

            return;
        }

        workflow.submitStep();
    }, [workflow]);

    /* -------------------------------------------------------------------------
       LEAVING

       Call from the popup's close button. With nothing recorded yet there is
       nothing to warn about, so it exits straight away.
    ------------------------------------------------------------------------- */

    const requestExit = useCallback(() => {
        if (workflow?.hasProgress) {
            setConfirmType(CONFIRM_TYPE.LEAVE);

            return;
        }

        exit();
    }, [workflow, exit]);

    /* -------------------------------------------------------------------------
       COPY
    ------------------------------------------------------------------------- */

    const dialogProps = useMemo(() => {
        if (!confirmType) {
            return { open: false };
        }

        const patientName = caseData?.patient_name || "this patient";

        const refNo = caseData?.report_id ? "#" + caseData.report_id : "";

        if (confirmType === CONFIRM_TYPE.LEAVE) {
            return {
                open: true,
                tone: CONFIRM_TONE.WARNING,
                title: "Are you sure you want to go back?",
                message:
                    "Your changes will be saved, but the case will remain open.",
                secondaryLabel: "Go Back",
                onSecondary: exit,
                primaryLabel: "Continue Editing",
                onPrimary: dismiss,
                onClose: dismiss,
            };
        }

        if (confirmType === CONFIRM_TYPE.CLOSE_MIDWAY) {
            const who = refNo
                ? patientName + " (" + refNo + ")"
                : patientName;

            return {
                open: true,
                tone: CONFIRM_TONE.WARNING,
                title: "Close this case?",
                message:
                    "You are closing this case midway. All steps have not been completed yet. Incomplete data may affect case records for " +
                    who +
                    ".",
                secondaryLabel: "Go Back",
                onSecondary: dismiss,
                primaryLabel: "Close Anyway",
                onPrimary: () => {
                    setConfirmType(null);
                    workflow?.submitStep({ confirmed: true });
                },
                onClose: dismiss,
            };
        }

        return {
            open: true,
            tone: CONFIRM_TONE.SUCCESS,
            title: "Case Closed Successfully",
            message:
                "Case " +
                (refNo || "record") +
                " for " +
                patientName +
                " has been closed and all records have been saved.",
            primaryLabel: "Got It",
            onPrimary: exit,
            onClose: exit,
        };
    }, [confirmType, caseData, workflow, dismiss, exit]);

    return {
        dialogProps,
        submitStep,
        requestExit,
    };
}
